import React, { type HTMLProps, useCallback, useEffect } from 'react';

import { AnalyticsEventActions, AnalyticsEventSource, useAnalytics } from '@atlassian/ptc-common';

import { getRelativeUrl, isSPALink, prefixWithContextEntryPoint } from '../../utils/url';
import { useAppConfig } from '../AppConfigContext';

const isModified = (event: React.MouseEvent<HTMLAnchorElement>) =>
	Boolean(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);

const getSpaAndRelativePath = (href: string, contextSpaPaths: string[]) => {
	const relativePath = getRelativeUrl(href);
	const isSPA = isSPALink(relativePath, contextSpaPaths);

	return {
		isSPA,
		relativePath,
	};
};

export const Link = ({ href, onClick, target, ...rest }: HTMLProps<HTMLAnchorElement>) => {
	const analytics = useAnalytics();
	const { contextSpaPaths, contextEntryPoint, push } = useAppConfig();

	useEffect(() => {
		if (!href) {
			analytics.pushOperationalEvent({
				source: AnalyticsEventSource.CLIENT_METRICS,
				actionSubject: 'link',
				action: AnalyticsEventActions.FAILED,
				attributes: {
					contextSpaPaths,
					contextEntryPoint,
				},
				tags: ['error'],
			});
		}
	}, [analytics, contextEntryPoint, contextSpaPaths, href]);

	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement>) => {
			if (onClick) {
				onClick(event);
			}

			if (href) {
				const { relativePath, isSPA } = getSpaAndRelativePath(href, contextSpaPaths);

				if (
					isSPA && // only use push for links that work for SPA transitions
					!event.defaultPrevented && // set when onClick called preventDefault
					event.button === 0 && // only use push for left mouse button clicks
					(!target || target === '_self') && // let browser handle "_blank" etc
					!isModified(event) // let browser handle clicks with modifier
				) {
					event.preventDefault();
					push(relativePath);
				}
			}
		},
		[onClick, href, contextSpaPaths, push, target],
	);

	if (href) {
		const { isSPA } = getSpaAndRelativePath(href, contextSpaPaths);

		const props = {
			href: prefixWithContextEntryPoint(href, contextEntryPoint),
			...(target ? { target } : isSPA ? {} : { target: '_blank' }),
			...(isSPA ? {} : { rel: 'noopener noreferrer' }),
		};

		// eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
		return <a {...rest} onClick={handleClick} {...props} />;
	}

	// if href is missing render <a> with original onClick handler
	// and without href or target
	// Note that we trigger an operational event in `componentDidMount` to monitor why `href` is not passed.
	// I have a suspicion that empty href can cause broken link
	// eslint-disable-next-line jsx-a11y/anchor-has-content, @atlaskit/design-system/no-html-anchor, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
	return <a {...rest} onClick={onClick} />;
};

export default Link;
